import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,   
  CircularProgress, 
  Typography, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  formatNumber, 
  getSelectedIds, 
  isAnyRowSelected 
} from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import IncomeAdjustmentsList from './IncomeAdjustmentsList';


const IncomeAdjustments = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "IncomeAdjustments", "create", false);
  const canEdit = hasRight(permissionMap, "IncomeAdjustments", "update", false);
  const canDelete = hasRight(permissionMap, "IncomeAdjustments", "delete", false);
  const canExport = hasRight(permissionMap, "IncomeAdjustments", "export", false);
  
  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });  
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  // const [data, setData] = useState([]);
  const [incomeAdjustments, setIncomeAdjustments] = useState([]);

  
  useEffect(() => {
    const handleIncomeAdjustments = async () => {
      try {
        if (!startDate || !endDate) return [];

        setError(null);
        setLoading(true);  

        const url = '/incomeadjustments';
        const payload = { startDate, endDate };
        const res = await api.post(url, payload);
  
        setIncomeAdjustments(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
  
    handleIncomeAdjustments();
  }, [startDate, endDate]); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,      
    },
    {
      field: "description",
      headerName: "Description",
      width: 300,
    },
    {
      field: "adjustmentType",
      headerName: "AdjustmentType",
      width: 150,
    },
    {
      field: "effectiveDate",
      headerName: "EffectiveDate",
      align: "center",
      width: 120,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${formatInTimeZone(value, 'UTC', "dd/MM/yyyy")}`;
      },
    },    
    {
      field: "amount",
      headerName: "Amount",
      width: 150,
      renderCell: (params) => {       
        return (
          <Box
            sx={{
              height: '100%',
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="body2">
              {formatNumber(params.value, 0, 0)}
            </Typography>            
          </Box>
        );
      }, 
    },       
    {
      field: "currency",
      headerName: "Currency",
      width: 100,
    },
    {
      field: 'crewMembers',
      headerName: 'CrewMembers',
      width: 360,      
      renderCell: (params) => {
        const sortedCrewMembers = [...params.row.crewMembers].sort((a, b) => a.crewCode - b.crewCode);
  
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {sortedCrewMembers?.map((member, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                alignContent='flex-start'
                gap={1}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '30px',
                  }}
                >
                  {member.crewCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {member.crewName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  ({member.rank} | {member.base})
                </Typography>                
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "crewCount",
      headerName: "CrewCount",
      align: "right",
      width: 100,     
      renderCell: (params) => (
        <span>
          {Array.isArray(params.row.crewMembers) 
            ? params.row.crewMembers.length 
            : 0
          }
        </span>
      )
    },
    {
      field: "isNonTaxableIncome",
      headerName: "IsNonTaxableIncome",
      width: 100,
    },    
  ], []);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,      
      sortable: false,
      width: 90,      
      renderCell: (params) => {        
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{ color: "primary.dark" }}
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/incomeadjustments/${params.row._id}`);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
           
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{ color: "primary.dark" }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);
                }}
              >
                <DeleteForeverIcon />
              </IconButton>                
            </Tooltip>
          </Box>
        );
      },
    },
  ];
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    if (!canDelete) return;

    const selectedIds = getSelectedIds(rowSelectionModel, incomeAdjustments);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/incomeadjustments/delete/${idToDelete}`);

        setIncomeAdjustments(incomeAdjustments.filter((item) => item._id !== idToDelete));
        toast.success('Income adjustment deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, incomeAdjustments);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const payload = { ids: selectedIds };
        await api.patch('/incomeadjustments/soft-delete', payload)
        
        setIncomeAdjustments(
          incomeAdjustments.filter(item => !selectedIds.includes(item._id))
        );
        toast.success(`${selectedIds.length} record(s) deleted successfully.`);        
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete!");
    }
  }, [
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    incomeAdjustments
  ]);
 
  const handleFileUpload = useCallback(async (e) => {
    setError(null);

    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        let jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        // Remove empty columns
        if (jsonData.length > 0) {
          const keys = Object.keys(jsonData[0]);
          const validKeys = keys.filter((key) =>
            jsonData.some((row) => row[key] !== "")
          );

          jsonData = jsonData.map((row) => {
            const filtered = {};
            validKeys.forEach((k) => (filtered[k] = row[k]));
            return filtered;
          });
        }

        if (!jsonData.length) {
          toast.error("Excel file is empty!");
          return;
        }

        // setData(jsonData);

        const url = '/incomeadjustments/upload';
        const payload = {          
          data: jsonData
        };

        await api.post(url, payload);

        toast.success("Upload income adjustments successfully.");
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || "Failed to upload data!");
      }
    };

    reader.onerror = () => toast.error("Error reading file!");
    reader.readAsArrayBuffer(file);
  }, []);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Income Adjustments" 
          subtitle={`${loading ? 'Loading...' : 'List of Adjustments'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Upload & save to DB" arrow>
              <span>
                <label htmlFor={"upload-file"}>
                  <StyledButton 
                    component="span" 
                  >
                    <FileUploadIcon />
                  </StyledButton>
                </label>
              </span>
            </Tooltip>
            <input
              id="upload-file"
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            /> 
          
            {canExport && (
              <Tooltip title="Download Excel file" arrow>
                <span>
                  <StyledButton 
                    onClick={() => {
                      const omitFields = ['deletedAt', 'updatedAt', 'createdAt', 'createdBy', '__v']
                      exportToExcel(incomeAdjustments, "IncomeAdjustments", omitFields);
                      logClientAction("EXPORT_REPORT", { fileName: "IncomeAdjustments", format: "Excel", startDate, endDate });
                    }} 
                  >
                    <DownloadIcon />
                  </StyledButton>
                </span>              
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton 
                    disabled={!isAnyRowSelected(rowSelectionModel, incomeAdjustments)}
                    onClick={() => {
                      if (!canDelete) return;
                      handleBulkDelete();
                    }}
                  >
                    <DeleteIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton 
                    disabled={!canCreate}
                    onClick={() => {
                      if (!canCreate) return;
                      navigate("/incomeadjustments/new");
                    }}
                  >
                    <AddCircleOutlineIcon />
                  </StyledButton>
                </span>
              </Tooltip>      
            )}      
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">
            Error: {error?.message || ''}
          </Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box
            ref={scrollRef} 
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <IncomeAdjustmentsList
                rows={incomeAdjustments || []}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/incomeadjustments/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={incomeAdjustments || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  initialState: {
                    pagination: { paginationModel: { pageSize: 100 } },
                  },
                  onRowSelectionModelChange: (newRowSelectionModel) => {
                    setRowSelectionModel(newRowSelectionModel);
                  },
                  rowSelectionModel,
                  getRowClassName: (params) =>
                    params.row.isActive === false ? 'isActive-false' : '',
                }}
                sx={{
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,
                  },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />

            <DeleteConfirm
              open={confirmDialogOpen}
              onClose={() => setConfirmDialogOpen(false)}
              onConfirm={executeDelete}
              deleteType={deleteType}
            />
          </Box>    
        </Box>
      )}
    </Box>
  );
};

export default IncomeAdjustments;
